import { useMemo, useState } from "react";

import {
  ChevronDownIcon,
  FilterIcon,
  V2Button,
  V2MultiSelectDropdown,
  ToggleButton,
  ToggleButtonGroup,
  type V2MultiDropdownOption,
  type Selection,
} from "@stacklet/ui";

import {
  cloudOrganizationPath,
  samlProviderEditPath,
  samlProviderPath,
} from "app/views/settings/access-control/IdentityProviders/constants";

import {
  GroupBandsGrid,
  type GroupBand,
  type GroupsView,
} from "./GroupBandsGrid";
import { useIdentityProviderSources } from "./useIdentityProviderSources";

/**
 * Applies a toggle selection. React Aria hands over a set; in single selection
 * mode it holds at most one key, and an empty set (the selected item clicked
 * again) keeps the current view rather than clearing it.
 */
function readView(keys: Selection): GroupsView | undefined {
  if (typeof keys === "string") return undefined;
  return [...keys][0] as GroupsView | undefined;
}

interface IdpFilterProps {
  /** Every provider that can be filtered on. */
  options: V2MultiDropdownOption[];
  /** The currently selected providers; empty means "all". */
  value: V2MultiDropdownOption[];
  /** Called when the selection changes. */
  onChange: (selected: V2MultiDropdownOption[]) => void;
}

/**
 * The "Identity Provider" filter: a multiselect of the providers present,
 * narrowing which provider bands the grid queries. An empty selection lists
 * every provider.
 */
function IdpFilter({ options, value, onChange }: IdpFilterProps) {
  const summary =
    value.length === 0 ? "All" : value.map((option) => option.name).join(", ");

  return (
    <V2MultiSelectDropdown
      label="Identity Provider"
      onChange={onChange}
      options={options}
      placement="bottom right"
      trigger={
        <V2Button
          IconLeft={<FilterIcon size={14} />}
          IconRight={<ChevronDownIcon size={12} />}
          aria-label="Filter by identity provider"
          className="whitespace-nowrap py-xxs"
          variant="tertiary"
        >
          <span className="text-text-secondary">Identity Provider:</span>{" "}
          {summary}
        </V2Button>
      }
      value={value}
    />
  );
}

/**
 * The settings groups directory: every provider's groups in per-provider bands,
 * plus a Local Groups band. The view toggle switches what an expanded group
 * shows (its role assignments or its members); the "Identity Provider" filter
 * narrows which provider bands are listed.
 *
 * Cloud orgs are a connection, so every page is loaded, not just the first.
 */
export function GroupsDirectory() {
  const { samlProviders, cloudOrganizations, loading } =
    useIdentityProviderSources();

  // The identity-provider bands: every SAML provider and cloud organization.
  const providerBands: GroupBand[] = useMemo(() => {
    const samlBands: GroupBand[] = samlProviders.map((provider) => {
      const label = provider.displayName || provider.idpAlias || provider.name;
      return {
        id: provider.id,
        label,
        secondary: provider.name,
        scope: { kind: "idp-namespace", idpNamespace: provider.idpNamespace },
        idpType: provider.idpType,
        idpDisplayName: label,
        to: samlProviderPath(provider.name),
        editTo: samlProviderEditPath(provider.name),
      };
    });

    const cloudBands: GroupBand[] = cloudOrganizations.map((node) => {
      // Label by the org's own name/alias: several orgs can share a provider
      // (e.g. multiple GCP orgs), so `provider` alone would show as duplicates.
      // The unique `key` stays as the secondary to tell them apart.
      const label = node.idpAlias || node.name;
      return {
        id: node.id,
        label,
        secondary: node.key,
        scope: { kind: "idp-namespace", idpNamespace: node.idpNamespace },
        idpType: node.idpType,
        idpDisplayName: label,
        to: cloudOrganizationPath(node.provider, node.key),
        editTo: null,
      };
    });

    return [...samlBands, ...cloudBands];
  }, [samlProviders, cloudOrganizations]);

  // Local groups (created in Stacklet, not synced from a provider) need their
  // own band; the exact idp-namespace scope would otherwise drop them entirely.
  const bands: GroupBand[] = useMemo(
    () => [
      ...providerBands,
      {
        id: "__local__",
        label: "Local Groups",
        secondary: null,
        scope: { kind: "local" },
        to: null,
        editTo: null,
      },
    ],
    [providerBands],
  );

  // Providers only, not the local band. `secondary` becomes assistive text so
  // providers sharing a label (e.g. same-named cloud orgs) stay distinguishable.
  const filterOptions: V2MultiDropdownOption[] = useMemo(
    () =>
      providerBands.map((band) => ({
        id: band.id,
        name: band.label,
        assistiveText: band.secondary ?? undefined,
      })),
    [providerBands],
  );

  const [view, setView] = useState<GroupsView>("role-assignments");
  const [selected, setSelected] = useState<V2MultiDropdownOption[]>([]);

  const handleViewChange = (keys: Selection) => {
    const next = readView(keys);
    if (next) setView(next);
  };

  const selectedIds = useMemo(
    () => new Set(selected.map((option) => option.id)),
    [selected],
  );
  const filteredBands = useMemo(
    () =>
      selectedIds.size === 0
        ? bands
        : bands.filter((band) => selectedIds.has(band.id)),
    [bands, selectedIds],
  );

  return (
    <div className="flex flex-col gap-md">
      <div className="flex items-center gap-sm">
        <span className="text-label-small text-text-secondary">View</span>
        <ToggleButtonGroup
          aria-label="View"
          onSelectionChange={handleViewChange}
          selectedKeys={new Set([view])}
          selectionMode="single"
        >
          <ToggleButton id="role-assignments">Role Assignments</ToggleButton>
          <ToggleButton id="users">Users</ToggleButton>
        </ToggleButtonGroup>
      </div>

      <GroupBandsGrid
        bands={filteredBands}
        loading={loading}
        tableId="settings-groups"
        toolbarActions={
          <IdpFilter
            onChange={setSelected}
            options={filterOptions}
            value={selected}
          />
        }
        view={view}
      />
    </div>
  );
}
